import { supabase } from '../lib/supabase';

/**
 * Real-time subscription service for Supabase tables
 * Provides methods to subscribe to live updates for vouchers, wishes, and transactions
 */

class RealtimeService {
  /**
   * Subscribe to real-time voucher updates
   * @param {Function} onInsert - Callback for new vouchers
   * @param {Function} onUpdate - Callback for voucher updates
   * @param {Function} onDelete - Callback for voucher deletions
   * @param {Object} filters - Optional filters (categoryId, availabilityType, etc.)
   * @returns {Object} Supabase channel subscription
   */
  subscribeToVouchers({ onInsert, onUpdate, onDelete, filters = {} }) {
    const channel = supabase?.channel('vouchers-realtime')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'vouchers',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('New voucher:', payload);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'vouchers',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('Voucher updated:', payload);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'vouchers',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('Voucher deleted:', payload);
          if (onDelete) onDelete(payload?.old);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Subscribe to real-time voucher wish updates
   * @param {Function} onInsert - Callback for new wishes
   * @param {Function} onUpdate - Callback for wish updates
   * @param {Function} onDelete - Callback for wish deletions
   * @param {Object} filters - Optional filters (userId, wishType, voucherId, etc.)
   * @returns {Object} Supabase channel subscription
   */
  subscribeToVoucherWishes({ onInsert, onUpdate, onDelete, filters = {} }) {
    const channel = supabase?.channel('voucher-wishes-realtime')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'voucher_wishes',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('New wish:', payload);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'voucher_wishes',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('Wish updated:', payload);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'voucher_wishes',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('Wish deleted:', payload);
          if (onDelete) onDelete(payload?.old);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Subscribe to real-time transaction updates
   * @param {Function} onInsert - Callback for new transactions
   * @param {Function} onUpdate - Callback for transaction updates
   * @param {Object} filters - Optional filters (buyerId, sellerId, status, etc.)
   * @returns {Object} Supabase channel subscription
   */
  subscribeToTransactions({ onInsert, onUpdate, filters = {} }) {
    const channel = supabase?.channel('transactions-realtime')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'transactions',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('New transaction:', payload);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'transactions',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('Transaction updated:', payload);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Subscribe to specific voucher by ID
   * @param {string} voucherId - Voucher ID to watch
   * @param {Function} onUpdate - Callback for updates
   * @returns {Object} Supabase channel subscription
   */
  subscribeToVoucherById(voucherId, onUpdate) {
    const channel = supabase?.channel(`voucher-${voucherId}`)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'vouchers',
          filter: `id=eq.${voucherId}`
        },
        (payload) => {
          console.log('Voucher updated:', payload);
          if (onUpdate) onUpdate(payload?.new);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Subscribe to user-specific transactions
   * @param {string} userId - User ID to watch
   * @param {Function} onInsert - Callback for new transactions
   * @param {Function} onUpdate - Callback for transaction updates
   * @returns {Object} Supabase channel subscription
   */
  subscribeToUserTransactions(userId, { onInsert, onUpdate }) {
    const channel = supabase?.channel(`user-transactions-${userId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'transactions',
          filter: `buyer_id=eq.${userId}`
        },
        (payload) => {
          console.log('New transaction (buyer):', payload);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'transactions',
          filter: `seller_id=eq.${userId}`
        },
        (payload) => {
          console.log('New transaction (seller):', payload);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'transactions',
          filter: `buyer_id=eq.${userId}`
        },
        (payload) => {
          console.log('Transaction updated (buyer):', payload);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'transactions',
          filter: `seller_id=eq.${userId}`
        },
        (payload) => {
          console.log('Transaction updated (seller):', payload);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Unsubscribe from a channel
   * @param {Object} channel - Supabase channel to unsubscribe
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll() {
    supabase?.removeAllChannels();
  }

  /**
   * Subscribe to real-time negotiation updates
   * @param {Function} onInsert - Callback for new negotiations
   * @param {Function} onUpdate - Callback for negotiation updates
   * @param {Object} filters - Optional filters (userId, wishId, status, etc.)
   * @returns {Object} Supabase channel subscription
   */
  subscribeToNegotiations({ onInsert, onUpdate, filters = {} }) {
    const channel = supabase?.channel('negotiations-realtime')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'negotiations',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('New negotiation:', payload);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'negotiations',
          filter: this._buildFilter(filters)
        },
        (payload) => {
          console.log('Negotiation updated:', payload);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Subscribe to real-time negotiation messages
   * @param {string} negotiationId - Negotiation ID to watch
   * @param {Function} onInsert - Callback for new messages
   * @param {Function} onUpdate - Callback for message updates (read status)
   * @returns {Object} Supabase channel subscription
   */
  subscribeToNegotiationMessages(negotiationId, { onInsert, onUpdate }) {
    const channel = supabase?.channel(`negotiation-messages-${negotiationId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'negotiation_messages',
          filter: `negotiation_id=eq.${negotiationId}`
        },
        (payload) => {
          console.log('New negotiation message:', payload);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'negotiation_messages',
          filter: `negotiation_id=eq.${negotiationId}`
        },
        (payload) => {
          console.log('Negotiation message updated:', payload);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Subscribe to user-specific negotiations (as initiator or responder)
   * @param {string} userId - User ID to watch
   * @param {Function} onInsert - Callback for new negotiations
   * @param {Function} onUpdate - Callback for negotiation updates
   * @returns {Object} Supabase channel subscription
   */
  subscribeToUserNegotiations(userId, { onInsert, onUpdate }) {
    const channel = supabase?.channel(`user-negotiations-${userId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'negotiations',
          filter: `initiator_id=eq.${userId}`
        },
        (payload) => {
          console.log('New negotiation (initiator):', payload);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'negotiations',
          filter: `responder_id=eq.${userId}`
        },
        (payload) => {
          console.log('New negotiation (responder):', payload);
          if (onInsert) onInsert(payload?.new);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'negotiations',
          filter: `initiator_id=eq.${userId}`
        },
        (payload) => {
          console.log('Negotiation updated (initiator):', payload);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'negotiations',
          filter: `responder_id=eq.${userId}`
        },
        (payload) => {
          console.log('Negotiation updated (responder):', payload);
          if (onUpdate) onUpdate(payload?.new, payload?.old);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Subscribe to trading alerts (transactions, wishes, negotiations)
   * Provides instant notifications for trading activities
   * @param {string} userId - User ID to watch
   * @param {Function} onTradingAlert - Callback for trading events
   * @returns {Object} Object containing all channel subscriptions
   */
  subscribeToTradingAlerts(userId, onTradingAlert) {
    // Subscribe to transactions
    const transactionChannel = this.subscribeToUserTransactions(userId, {
      onInsert: (transaction) => {
        onTradingAlert({
          type: 'transaction',
          event: 'new',
          data: transaction,
          message: 'Nova transakcija'
        });
      },
      onUpdate: (transaction, oldTransaction) => {
        onTradingAlert({
          type: 'transaction',
          event: 'update',
          data: transaction,
          oldData: oldTransaction,
          message: 'Transakcija ažurirana'
        });
      }
    });

    // Subscribe to negotiations
    const negotiationChannel = this.subscribeToUserNegotiations(userId, {
      onInsert: (negotiation) => {
        onTradingAlert({
          type: 'negotiation',
          event: 'new',
          data: negotiation,
          message: 'Novi pregovor pokrenut'
        });
      },
      onUpdate: (negotiation, oldNegotiation) => {
        onTradingAlert({
          type: 'negotiation',
          event: 'update',
          data: negotiation,
          oldData: oldNegotiation,
          message: 'Pregovor ažuriran'
        });
      }
    });

    // Subscribe to wishes
    const wishChannel = this.subscribeToVoucherWishes({
      onInsert: (wish) => {
        onTradingAlert({
          type: 'wish',
          event: 'new',
          data: wish,
          message: 'Nova želja objavljena'
        });
      },
      onUpdate: (wish, oldWish) => {
        onTradingAlert({
          type: 'wish',
          event: 'update',
          data: wish,
          oldData: oldWish,
          message: 'Želja ažurirana'
        });
      }
    });

    return {
      transactionChannel,
      negotiationChannel,
      wishChannel,
      unsubscribe: () => {
        this.unsubscribe(transactionChannel);
        this.unsubscribe(negotiationChannel);
        this.unsubscribe(wishChannel);
      }
    };
  }

  /**
   * Subscribe to typing indicators for a negotiation
   * @param {string} negotiationId - Negotiation ID to watch
   * @param {Function} onTypingChange - Callback for typing status changes
   * @returns {Object} Supabase channel subscription
   */
  subscribeToTypingIndicators(negotiationId, onTypingChange) {
    const channel = supabase?.channel(`typing-${negotiationId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'typing_indicators',
          filter: `negotiation_id=eq.${negotiationId}`
        },
        (payload) => {
          console.log('Typing indicator changed:', payload);
          if (onTypingChange) onTypingChange(payload);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Build filter string for Supabase realtime
   * @private
   */
  _buildFilter(filters) {
    if (!filters || Object.keys(filters)?.length === 0) {
      return undefined;
    }

    const filterParts = [];
    Object.entries(filters)?.forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        filterParts?.push(`${key}=eq.${value}`);
      }
    });

    return filterParts?.length > 0 ? filterParts?.join(',') : undefined;
  }
}

// Export singleton instance
export default new RealtimeService();